import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Infringement} from '../intefaces/infringement.interface';
import {InfringementDTO} from '../intefaces/infringementDTO.interface';
import {InfringementListItem} from '../intefaces/infringementListItem.interface';
import {environment} from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InfringementService {
  // Define the API endpoint.
  private readonly headers: HttpHeaders;

  constructor(private http: HttpClient) {
    this.headers = new HttpHeaders().set('Authorization', 'Bearer Gustav');
  }

  /**
   * @returns An Observable of Infringement.
   */
  getInfringement(identifier: string): Observable<Infringement> {
    // The request body to send with the GET request.
    const params = new HttpParams()
      .set('resource', 'Infringement')
      .set('identifier', identifier.toLowerCase());
    const headers = this.headers;
    return this.http.get<Infringement>(environment.apiUrl, {headers, params});
  }

  getInfringementListItems(): Observable<any> {
    const params = new HttpParams().set('resource', 'InfringementListItem')
    const headers = this.headers;
    return this.http.get<InfringementListItem[]>(environment.apiUrl, {headers, params});
  }

  add(infringement: InfringementDTO): Observable<Infringement> {
    const params = new HttpParams().set('resource', 'Infringement');
    const headers = this.headers;
    return this.http.post<Infringement>(environment.apiUrl, infringement, {headers, params});
  }

  update(infringement: InfringementDTO): Observable<Infringement> {
    const params = new HttpParams().set('resource', 'Infringement');
    const headers = this.headers;
    return this.http.put<Infringement>(environment.apiUrl, infringement, {headers, params});
  }

  addComment(commentText: string, infringementIdentifier: string) {

    const commentDTO = {
      "identifier": infringementIdentifier,
      "message": commentText
    }

    const params = new HttpParams().set('resource', 'Comment');
    const headers = this.headers;
    return this.http.post<any>(environment.apiUrl, commentDTO, {headers, params});
  }

  /**
   * Uploads a file via presigned S3 URL (3-step flow):
   * 1. Get presigned upload URL from backend
   * 2. PUT file directly to S3 (with progress tracking)
   * 3. Confirm upload in backend
   *
   * @param onStep  called when a new step begins (1, 2, or 3)
   * @param onProgress  called during S3 upload with percent (0-100)
   * @param signal  AbortSignal to cancel the upload
   */
  private static readonly MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100 MB

  async uploadFile(
    file: File,
    filename: string,
    identifier: string,
    onProgress?: (percent: number, loaded: number, total: number) => void,
    signal?: AbortSignal,
    onConfirming?: () => void
  ): Promise<void> {
    // Sanitize filename: only [a-zA-Z0-9\-_.] allowed
    const sanitizedFileName = filename.replace(/[^a-zA-Z0-9\-_.]/g, '_');

    if (file.size >= InfringementService.MULTIPART_THRESHOLD) {
      await this.uploadMultipart(file, sanitizedFileName, identifier, onProgress, signal, onConfirming);
    } else {
      await this.uploadSinglePut(file, sanitizedFileName, identifier, onProgress, signal, onConfirming);
    }
  }

  private async uploadSinglePut(
    file: File,
    sanitizedFileName: string,
    identifier: string,
    onProgress?: (percent: number, loaded: number, total: number) => void,
    signal?: AbortSignal,
    onConfirming?: () => void
  ): Promise<void> {
    // Step 1: Get presigned upload URL (with contentType + contentLength for S3 signature)
    let params = new HttpParams()
      .set('resource', 'AttachmentUploadUrl')
      .set('identifier', identifier)
      .set('fileName', sanitizedFileName);

    if (file.type) {
      params = params.set('contentType', file.type);
    }
    params = params.set('contentLength', file.size.toString());

    const headers = this.headers;

    if (signal?.aborted) throw new Error('Upload cancelled');

    const urlResponse = await this.http
      .get<{ success: boolean; uploadUrl: string; expiresIn: number; contentType?: string }>(
        environment.apiUrl, { headers, params }
      )
      .toPromise();

    if (!urlResponse?.uploadUrl) {
      throw new Error('Failed to get upload URL');
    }

    // Step 2: Upload file directly to S3 with progress
    const uploadContentType = urlResponse.contentType || 'application/octet-stream';
    if (signal?.aborted) throw new Error('Upload cancelled');

    await this.uploadToS3(urlResponse.uploadUrl, file, uploadContentType, onProgress, signal, onConfirming);

    // Step 3: Confirm upload in backend
    if (signal?.aborted) throw new Error('Upload cancelled');
    onConfirming?.();

    const confirmParams = new HttpParams().set('resource', 'AttachmentConfirm');
    await this.http
      .post<any>(environment.apiUrl, {
        identifier,
        fileName: sanitizedFileName
      }, { headers, params: confirmParams })
      .toPromise();
  }

  private async uploadMultipart(
    file: File,
    sanitizedFileName: string,
    identifier: string,
    onProgress?: (percent: number, loaded: number, total: number) => void,
    signal?: AbortSignal,
    onConfirming?: () => void
  ): Promise<void> {
    const headers = this.headers;

    // Step 1: Init multipart upload
    if (signal?.aborted) throw new Error('Upload cancelled');

    const initParams = new HttpParams().set('resource', 'MultipartUploadInit');
    const initResponse = await this.http
      .post<{
        uploadId: string;
        chunkSize: number;
        totalParts: number;
        partUrls: { partNumber: number; url: string }[];
      }>(environment.apiUrl, {
        identifier,
        fileName: sanitizedFileName,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size
      }, { headers, params: initParams })
      .toPromise();

    if (!initResponse?.uploadId || !initResponse.partUrls?.length) {
      throw new Error('Failed to init multipart upload');
    }

    const { uploadId, chunkSize, partUrls } = initResponse;

    // Step 2: Upload all parts in parallel (max 3 concurrent)
    const completedParts: { partNumber: number; etag: string }[] = [];
    const totalSize = file.size;
    let completedBytes = 0;

    const uploadPart = async (part: { partNumber: number; url: string }): Promise<void> => {
      if (signal?.aborted) throw new Error('Upload cancelled');

      const start = (part.partNumber - 1) * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      const partSize = end - start;

      const etag = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (!etag) {
              reject(new Error(`No ETag returned for part ${part.partNumber}`));
            } else {
              // Count progress only when S3 confirms the part
              completedBytes += partSize;
              onProgress?.((completedBytes / totalSize) * 100, completedBytes, totalSize);
              resolve(etag);
            }
          } else if (xhr.status === 403) {
            reject(new Error('Upload URL expired. Please try again.'));
          } else {
            reject(new Error(`Part ${part.partNumber} upload failed: ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error(`Network error on part ${part.partNumber}`)));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

        if (signal) {
          signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }

        xhr.open('PUT', part.url);
        xhr.send(blob);
      });

      completedParts.push({ partNumber: part.partNumber, etag });
    };

    // Upload with max 3 concurrent parts
    const concurrency = 3;
    const queue = [...partUrls];

    try {
      const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
        while (queue.length > 0) {
          const part = queue.shift()!;
          await uploadPart(part);
        }
      });

      await Promise.all(workers);
    } catch (error: any) {
      // Abort multipart upload on failure
      try {
        const abortParams = new HttpParams().set('resource', 'MultipartUploadAbort');
        await this.http.post<any>(environment.apiUrl, {
          identifier, fileName: sanitizedFileName, uploadId
        }, { headers, params: abortParams }).toPromise();
      } catch { /* best effort cleanup */ }
      throw error;
    }

    // Step 3: Complete multipart upload
    if (signal?.aborted) {
      const abortParams = new HttpParams().set('resource', 'MultipartUploadAbort');
      await this.http.post<any>(environment.apiUrl, {
        identifier, fileName: sanitizedFileName, uploadId
      }, { headers, params: abortParams }).toPromise();
      throw new Error('Upload cancelled');
    }

    onConfirming?.();

    const completeParams = new HttpParams().set('resource', 'MultipartUploadComplete');
    const sortedParts = completedParts.sort((a, b) => a.partNumber - b.partNumber);

    await this.http
      .post<any>(environment.apiUrl, {
        identifier,
        fileName: sanitizedFileName,
        uploadId,
        parts: sortedParts
      }, { headers, params: completeParams })
      .toPromise();
  }

  private uploadToS3(
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress?: (percent: number, loaded: number, total: number) => void,
    signal?: AbortSignal,
    onWaitingForResponse?: () => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress?.((e.loaded / e.total) * 100, e.loaded, e.total);
        }
      });

      // All bytes sent, now waiting for S3 response
      xhr.upload.addEventListener('load', () => {
        onWaitingForResponse?.();
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else if (xhr.status === 403) {
          reject(new Error('Upload URL expired. Please try again.'));
        } else {
          reject(new Error(`S3 upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

      if (signal) {
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  }

  /**
   * Downloads a file via presigned S3 URL:
   * 1. Get presigned download URL from backend
   * 2. Trigger browser download via anchor element
   */
  async downloadFile(filename: string, identifier: string): Promise<void> {
    const params = new HttpParams()
      .set('resource', 'AttachmentDownloadUrl')
      .set('identifier', identifier.toLowerCase())
      .set('fileName', filename);
    const headers = this.headers;

    const res = await this.http
      .get<{ success: boolean; downloadUrl: string; expiresIn: number }>(
        environment.apiUrl, { headers, params }
      )
      .toPromise();

    if (!res?.downloadUrl) {
      throw new Error('Failed to get download URL');
    }

    const link = document.createElement('a');
    link.href = res.downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  deleteFile(filename: string, identifier: string) {
    const params = new HttpParams()
      .set('resource', 'Attachment')
      .set('identifier', identifier.toLowerCase())
      .set('fileName', filename);
    const headers = this.headers;
    return this.http.delete<any>(environment.apiUrl, {headers, params});
  }

  getTemplateText(identifier: string): Observable<WarningEmailTemplate> {
    const params = new HttpParams()
    .set('resource', 'InfringementEmail')
    .set('identifier', identifier.toLowerCase())
    const headers = this.headers;
    return this.http.get<WarningEmailTemplate>(environment.apiUrl, {headers, params});
  }

  sendMail(payload: SendMailPayload): Observable<void> {
    const params = new HttpParams().set('resource', 'MailSend');
    const headers = this.headers;
    return this.http.post<void>(environment.apiUrl, payload, {headers, params});
  }

  infringementToDTO(infringement: Infringement): InfringementDTO {
    return {
      attachments: infringement.attachments ?? null,
      identifier: infringement.identifier,
      contractValue: infringement.contractValue ?? null,

      accused: {
        name: infringement.accused.name,
        url: infringement.accused.url,
        email: infringement.accused.email,
        country: infringement.accused.country,
        countryCode: infringement.accused.countryCode,
        address: infringement.accused.address ?? null,
      },

      debitor: infringement.debitor
        ? {
          ID: infringement.debitor.ID,
          invoiceAmmount: infringement.debitor.invoiceAmmount,
        }
        : null,

      infringementType: infringement.infringementType,
      infringementStatus: infringement.infringementStatus,
      infringementDetails: infringement.infringementDetails,

      genericComments: infringement.genericComments
        ? infringement.genericComments.map((c) => ({
          date: this.toNullableIsoString(c.date)!,
          comment: c.comment,
        }))
        : null,

      responsibleEmployee: infringement.responsibleEmployee ?? null,
      lastUpdatedBy: infringement.lastUpdatedBy ?? null,
      externalLawyer: infringement.externalLawyer ?? null,

      itSecurityCheck: {
        checked: infringement.itSecurityCheck.checked,
        itSecurityComments: infringement.itSecurityCheck.itSecurityComments
          ? infringement.itSecurityCheck.itSecurityComments.map((c) => ({
            date: this.toNullableIsoString(c.date)!,
            comment: c.comment,
          }))
          : null,
        orderID: infringement.itSecurityCheck.orderID
          ? [...infringement.itSecurityCheck.orderID]
          : null,
      },

      // alle Datumsfelder jetzt als ISO-String oder null
      dateCreated: this.toNullableIsoString(infringement.dateCreated),
      dateClosed: this.toNullableIsoString(infringement.dateClosed),
      warningSent: this.toNullableIsoString(infringement.warningSent),
      reminderDate: this.toNullableIsoString(infringement.reminderDate),
      secondReminderDate: this.toNullableIsoString(infringement.secondReminderDate),
    };
  }

  infringementExists(identifier: string) {
    const params = new HttpParams()
    .set('resource', 'InfringementExists')
    .set('identifier', identifier.toLowerCase())
    const headers = this.headers;
    return this.http.get<any>(environment.apiUrl, {headers, params});
  }

  getNextIdentifier(infringementType: number): Observable<{ identifier: string }> {
    const params = new HttpParams()
      .set('resource', 'NextIdentifier')
      .set('infringementType', infringementType.toString());
    const headers = this.headers;
    return this.http.get<{ identifier: string }>(environment.apiUrl, {headers, params});
  }

  private toNullableIsoString(value: Date | string | null | undefined): string | null {
    if (value == null) return null;

    const date = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(date.getTime())) return null;

    return date.toISOString().split('T')[0];
  }
}

export interface WarningEmailTemplate {
  from: string;
  to: string;
  html: string;
  subject: string;
}

export interface SendMailPayload {
  identifier: string;
  subject?: string;
  body?: string;
  attachmentFileNames?: string[];
}
